import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface ExpandableTextProps {
  text: string;
  className?: string;
  maxLines?: number;
}

export function ExpandableText({ text, className, maxLines = 3 }: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  useEffect(() => {
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (el) {
        setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
      }
    });
  }, [text, expanded]);

  const clampClass =
    maxLines === 3
      ? "line-clamp-3"
      : maxLines === 2
      ? "line-clamp-2"
      : maxLines === 4
      ? "line-clamp-4"
      : maxLines === 5
      ? "line-clamp-5"
      : "line-clamp-3";

  return (
    <div>
      <p
        ref={ref}
        className={cn(
          "whitespace-pre-wrap",
          !expanded && clampClass,
          className
        )}
      >
        {text}
      </p>
      {(isOverflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-xs text-primary mt-1 hover:underline focus:outline-none"
        >
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}
